import { useCallback, useMemo, useState } from 'react'
import { Modal } from 'antd'
import { Toast } from '@/components/Toast'
import { ID } from '@/types/common'
import {
  createVersionBatchColumns,
  createVersionColumns,
} from '../columns'
import classRecordApi from '../api'
import {
  StudentClassConfigVersion,
  StudentClassGenerationBatch,
} from '../types'

type UseClassRecordVersionManagementOptions = {
  configVersions: StudentClassConfigVersion[]
  generationBatches: StudentClassGenerationBatch[]
  selectedStudentId: ID | undefined
  loadStudentConfig: (studentId: ID) => Promise<void>
  loadRecords: () => Promise<void>
}

export function useClassRecordVersionManagement({
  configVersions,
  generationBatches,
  selectedStudentId,
  loadStudentConfig,
  loadRecords,
}: UseClassRecordVersionManagementOptions) {
  const [selectedVersionBatchDrawerId, setSelectedVersionBatchDrawerId] =
    useState<ID | null>(null)
  const [deletingBatchId, setDeletingBatchId] = useState<ID | null>(null)
  const [deletingVersionId, setDeletingVersionId] = useState<ID | null>(null)

  const handleDeleteGenerationBatch = useCallback(
    (batchId: ID) => {
      Modal.confirm({
        title: '确认回滚',
        content: '将删除该批次生成的上课记录（仅影响该批次），是否继续？',
        okText: '确认回滚',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            setDeletingBatchId(batchId)
            const res = await classRecordApi.deleteGenerationBatch(batchId)
            Toast.success(res?.message || '回滚成功')
            if (selectedStudentId) {
              await Promise.all([loadStudentConfig(selectedStudentId), loadRecords()])
            }
          } catch (error) {
            Toast.error('回滚失败')
            console.error('回滚失败:', error)
          } finally {
            setDeletingBatchId(null)
          }
        },
      })
    },
    [loadRecords, loadStudentConfig, selectedStudentId],
  )

  const handleDeleteConfigVersion = useCallback(
    (version: StudentClassConfigVersion) => {
      Modal.confirm({
        title: '确认删除配置版本',
        content: '删除后不可恢复，是否继续？',
        okText: '确认删除',
        cancelText: '取消',
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            setDeletingVersionId(version.id)
            const res = await classRecordApi.deleteConfigVersion(version.id)
            Toast.success(res?.message || '删除成功')
            if (selectedStudentId) {
              await loadStudentConfig(selectedStudentId)
            }
          } catch (error) {
            Toast.error('删除配置版本失败')
            console.error('删除配置版本失败:', error)
          } finally {
            setDeletingVersionId(null)
          }
        },
      })
    },
    [loadStudentConfig, selectedStudentId],
  )

  const handleSetCurrentVersion = useCallback(
    async (version: StudentClassConfigVersion) => {
      if (!selectedStudentId) {
        return
      }
      try {
        await classRecordApi.setCurrentVersion(selectedStudentId, version.id)
        Toast.success('已设为当前配置')
        await loadStudentConfig(selectedStudentId)
      } catch (error) {
        Toast.error('设置失败')
        console.error('设置当前配置失败:', error)
      }
    },
    [loadStudentConfig, selectedStudentId],
  )

  const generationBatchMonthlyPriceMap = useMemo(
    () =>
      generationBatches.reduce<Record<string, number | null>>((acc, batch) => {
        acc[String(batch.id)] = batch.monthlyPrice ?? null
        return acc
      }, {}),
    [generationBatches],
  )

  const configVersionMap = useMemo(
    () =>
      configVersions.reduce<Record<string, StudentClassConfigVersion>>(
        (acc, version) => {
          acc[String(version.id)] = version
          return acc
        },
        {},
      ),
    [configVersions],
  )

  const versionColumns = useMemo(
    () =>
      createVersionColumns({
        deletingVersionId,
        selectedStudentId,
        onDelete: handleDeleteConfigVersion,
        onReloadCurrentConfig: loadStudentConfig,
        onViewGenerationBatches: (version) => {
          setSelectedVersionBatchDrawerId(version.id)
        },
      }),
    [
      deletingVersionId,
      handleDeleteConfigVersion,
      loadStudentConfig,
      selectedStudentId,
    ],
  )

  const versionBatchColumns = useMemo(
    () =>
      createVersionBatchColumns({
        deletingBatchId,
        onDelete: handleDeleteGenerationBatch,
      }),
    [deletingBatchId, handleDeleteGenerationBatch],
  )

  const selectedVersionBatchDrawer = useMemo(() => {
    if (selectedVersionBatchDrawerId === null) {
      return null
    }
    return configVersionMap[String(selectedVersionBatchDrawerId)] ?? null
  }, [configVersionMap, selectedVersionBatchDrawerId])

  return {
    selectedVersionBatchDrawerId,
    setSelectedVersionBatchDrawerId,
    selectedVersionBatchDrawer,
    generationBatchMonthlyPriceMap,
    deletingVersionId,
    handleDeleteConfigVersion,
    handleSetCurrentVersion,
    versionColumns,
    versionBatchColumns,
  }
}
